import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router";
import { useTranslation } from "react-i18next";
import {
  Activity,
  AlertTriangle,
  ArrowRight,
  CheckCircle2,
  ChevronRight,
  Clock,
  Database,
  FileCode2,
  FileSpreadsheet,
  Inbox,
  ListChecks,
  Loader2,
  PackagePlus,
  Printer,
  RefreshCw,
  RotateCw,
  ScanLine,
  Sparkles,
} from "lucide-react";

import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Skeleton } from "./ui/skeleton";
import { ScrollArea } from "./ui/scroll-area";

import { useAuth } from "@/hooks/useAuth";
import { useImpressaoConfig } from "@/contexts/ImpressaoConfigContext";
import { useLeitores } from "@/contexts/LeitoresContext";
import { useContext } from "react";
import { FilaImpressaoSocketContext } from "@/contexts/FilaImpressaoSocketContext";
import impressao_socket_api from "@/services/impressao_socket_api";
import { formatarData } from "@/utils/UtilFuncoes";

type RecentOrder = {
  nome_da_ordem: string;
  estado_da_ordem: string;
  hardware_id: string;
  data_criacao: string;
  data_ultima_atualizacao: string;
  etiquetas_total: unknown[];
};

type StatusVariant = "success" | "info" | "warning" | "destructive" | "muted";
const statusBadgeClasses: Record<StatusVariant, string> = {
  success:
    "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/20",
  info: "bg-blue-500/10 text-blue-700 dark:text-blue-400 border-blue-500/20",
  warning:
    "bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/20",
  destructive: "bg-red-500/10 text-red-700 dark:text-red-400 border-red-500/20",
  muted: "bg-muted text-muted-foreground border-border",
};

function getStatusVariant(estado: string | undefined): StatusVariant {
  if (!estado) return "muted";
  if (estado === "Finalizado") return "success";
  if (estado === "Imprimindo") return "info";
  if (estado.toLowerCase().includes("erro")) return "destructive";
  return "warning";
}

function getGreetingKey() {
  const h = new Date().getHours();
  if (h >= 5 && h < 12) return "dashboard.greetingMorning";
  if (h >= 12 && h < 18) return "dashboard.greetingAfternoon";
  return "dashboard.greetingEvening";
}

function formatToday(locale: string) {
  return new Date().toLocaleDateString(locale, {
    weekday: "long",
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
}

function daysAgoIso(days: number) {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().split("T")[0];
}

const quickActions: Array<{
  labelKey: string;
  href: string;
  icon: typeof Printer;
  descKey: string;
  accent: string;
}> = [
  {
    labelKey: "nav.items.impressaoAvulsa",
    href: "/impressao-avulsa",
    icon: Printer,
    descKey: "dashboard.actions.impressaoAvulsaDesc",
    accent: "text-blue-500",
  },
  {
    labelKey: "nav.items.importarPlanilha",
    href: "/importar-planilha",
    icon: FileSpreadsheet,
    descKey: "dashboard.actions.importarPlanilhaDesc",
    accent: "text-emerald-500",
  },
  {
    labelKey: "nav.items.importarXml",
    href: "/importar-xml",
    icon: FileCode2,
    descKey: "dashboard.actions.importarXmlDesc",
    accent: "text-violet-500",
  },
  {
    labelKey: "nav.items.importarPedidos",
    href: "/importar-pedidos",
    icon: PackagePlus,
    descKey: "dashboard.actions.importarPedidosDesc",
    accent: "text-orange-500",
  },
  {
    labelKey: "nav.items.importarOrdem",
    href: "/importar-ordem",
    icon: ListChecks,
    descKey: "dashboard.actions.importarOrdemDesc",
    accent: "text-cyan-500",
  },
  {
    labelKey: "nav.items.reimprimirEtiqueta",
    href: "/reimprimir-etiqueta",
    icon: RotateCw,
    descKey: "dashboard.actions.reimprimirEtiquetaDesc",
    accent: "text-yellow-500",
  },
  {
    labelKey: "nav.items.conferirEtiqueta",
    href: "/conferir-etiqueta",
    icon: ScanLine,
    descKey: "dashboard.actions.conferirEtiquetaDesc",
    accent: "text-pink-500",
  },
  {
    labelKey: "nav.items.logsImpressao",
    href: "/logs-impressao",
    icon: Activity,
    descKey: "dashboard.actions.logsImpressaoDesc",
    accent: "text-slate-500",
  },
];

export function HomeDashboardComponent() {
  const { t, i18n } = useTranslation();
  const localeData = i18n.language === "en" ? "en-US" : "pt-BR";
  const { usuario } = useAuth();
  const { impressaoConfig, verificarSeEstaProximoDeAcabarORolo } =
    useImpressaoConfig();
  const { leitores } = useLeitores();
  const filaCtx = useContext(FilaImpressaoSocketContext);

  const [loadingStats, setLoadingStats] = useState(false);
  const [statsErro, setStatsErro] = useState(false);
  const [total7d, setTotal7d] = useState(0);
  const [finalizadas7d, setFinalizadas7d] = useState(0);
  const [ultimasOrdens, setUltimasOrdens] = useState<RecentOrder[]>([]);
  const [refreshKey, setRefreshKey] = useState(0);

  const fetchEstatisticas = useCallback(async () => {
    if (!impressaoConfig?.app_id || !impressaoConfig?.id_conta) return;
    setLoadingStats(true);
    setStatsErro(false);
    try {
      const di = daysAgoIso(7);
      const df = daysAgoIso(0);
      const url = `/v1/impressao/${encodeURIComponent(impressaoConfig.app_id)}?id_conta=${impressaoConfig.id_conta}&pagina=1&quantidade_por_pagina=5&estado=*&data_inicio=${di}&data_fim=${df}`;
      const response = await impressao_socket_api.get(url);
      if (response.data) {
        setTotal7d(response.data.total ?? 0);
        setFinalizadas7d(response.data.ordens_finalizadas ?? 0);
        const lista: RecentOrder[] = (response.data.ordens ?? [])
          .slice(0, 5)
          .map((o: RecentOrder) => o);
        setUltimasOrdens(lista);
      }
    } catch (e) {
      console.log(e);
      setStatsErro(true);
    } finally {
      setLoadingStats(false);
    }
  }, [impressaoConfig?.app_id, impressaoConfig?.id_conta]);

  useEffect(() => {
    fetchEstatisticas();
  }, [fetchEstatisticas, refreshKey]);

  useEffect(() => {
    if (impressaoConfig?.app_id && filaCtx?.coletarItemFila) {
      filaCtx.coletarItemFila().catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [impressaoConfig?.app_id, refreshKey]);

  const greeting = t(getGreetingKey());
  const hojeStr = useMemo(() => formatToday(localeData), [localeData]);

  const leitorAtual = leitores.find(
    (l) => l.serial === impressaoConfig?.app_id,
  );

  const etiquetasImpressas = impressaoConfig?.etiquetas_impressas ?? 0;
  const tamanhoRolo = impressaoConfig?.tamanho_rolo ?? 0;
  const restanteRolo = Math.max(0, tamanhoRolo - etiquetasImpressas);
  const percentualRolo =
    tamanhoRolo > 0
      ? Math.min(100, Math.round((etiquetasImpressas / tamanhoRolo) * 100))
      : 0;
  const roloAcabando = verificarSeEstaProximoDeAcabarORolo();

  const itensFila = filaCtx?.itensFila ?? [];
  const comFalha7d = Math.max(0, total7d - finalizadas7d);
  const taxaSucesso =
    total7d > 0 ? Math.round((finalizadas7d / total7d) * 100) : 0;

  const etiquetaPadraoNome = useMemo(() => {
    if (!impressaoConfig?.lista_zpl || !impressaoConfig?.etiqueta_padrao)
      return "—";
    const idx = parseInt(impressaoConfig.etiqueta_padrao);
    return impressaoConfig.lista_zpl[idx]?.nome || `Etiqueta ${idx + 1}`;
  }, [impressaoConfig?.lista_zpl, impressaoConfig?.etiqueta_padrao]);

  return (
    <div className="space-y-4">
      {/* Greeting / Hero */}
      <Card className="overflow-hidden border-none bg-gradient-to-br from-primary/5 via-card to-card">
        <CardContent className="p-5 sm:p-6">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="min-w-0">
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Sparkles className="h-3.5 w-3.5 text-primary" />
                <span className="capitalize">{hojeStr}</span>
              </div>
              <h1 className="mt-1 text-2xl font-semibold tracking-tight sm:text-3xl">
                {greeting},{" "}
                <span className="text-primary">
                  {usuario?.nome?.split(" ")[0] || t("dashboard.fallbackUser")}
                </span>
                <span className="text-muted-foreground">.</span>
              </h1>
              <p className="mt-1 text-sm text-muted-foreground">
                {t("dashboard.summary")}
              </p>
            </div>

            <div className="flex flex-wrap gap-2">
              <Badge
                variant="outline"
                className={statusBadgeClasses.info}
                title={t("dashboard.profileTitle")}
              >
                <Printer className="h-3 w-3 mr-1" />
                {impressaoConfig?.descricao_config || t("dashboard.noProfile")}
              </Badge>
              <Button
                size="sm"
                variant="outline"
                disabled={loadingStats}
                onClick={() => setRefreshKey((k) => k + 1)}
              >
                <RefreshCw
                  className={`h-4 w-4 ${loadingStats ? "animate-spin" : ""}`}
                />
                <span className="hidden sm:inline">{t("common.update")}</span>
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats - 4 cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Card className="hover:shadow-md transition-shadow">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                {t("dashboard.stats.last7days")}
              </CardTitle>
              <Activity className="h-4 w-4 text-muted-foreground" />
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            {loadingStats ? (
              <Skeleton className="h-8 w-16" />
            ) : (
              <div className="text-2xl font-bold">{total7d}</div>
            )}
            <p className="text-xs text-muted-foreground mt-1">
              {t("dashboard.stats.ordersPrinted")}
            </p>
          </CardContent>
        </Card>

        <Card className="hover:shadow-md transition-shadow">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                {t("dashboard.stats.successRate")}
              </CardTitle>
              <CheckCircle2 className="h-4 w-4 text-emerald-500" />
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            {loadingStats ? (
              <Skeleton className="h-8 w-16" />
            ) : (
              <div className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">
                {total7d > 0 ? `${taxaSucesso}%` : "—"}
              </div>
            )}
            <p className="text-xs text-muted-foreground mt-1">
              {t("dashboard.stats.finished", { count: finalizadas7d })}
            </p>
          </CardContent>
        </Card>

        <Card className="hover:shadow-md transition-shadow">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                {t("dashboard.stats.failed")}
              </CardTitle>
              <AlertTriangle className="h-4 w-4 text-red-500" />
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            {loadingStats ? (
              <Skeleton className="h-8 w-16" />
            ) : (
              <div
                className={`text-2xl font-bold ${
                  comFalha7d > 0
                    ? "text-red-600 dark:text-red-400"
                    : "text-foreground"
                }`}
              >
                {comFalha7d}
              </div>
            )}
            <p className="text-xs text-muted-foreground mt-1">
              {comFalha7d > 0
                ? t("dashboard.stats.checkLogs")
                : t("dashboard.stats.allGood")}
            </p>
          </CardContent>
        </Card>

        <Card className="hover:shadow-md transition-shadow">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                {t("dashboard.stats.currentQueue")}
              </CardTitle>
              <Inbox className="h-4 w-4 text-blue-500" />
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="text-2xl font-bold">{itensFila.length}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {itensFila.length === 1
                ? t("dashboard.stats.onePending")
                : t("dashboard.stats.manyPending")}
            </p>
          </CardContent>
        </Card>
      </div>

      {statsErro && (
        <Card className="border-amber-500/30 bg-amber-500/5">
          <CardContent className="p-3 flex items-center gap-2 text-xs text-amber-700 dark:text-amber-400">
            <AlertTriangle className="h-4 w-4" />
            {t("dashboard.statsError")}
          </CardContent>
        </Card>
      )}

      {/* Rolo de etiquetas */}
      {tamanhoRolo > 0 && (
        <Card
          className={
            roloAcabando ? "border-amber-500/40 bg-amber-500/5" : undefined
          }
        >
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between gap-2">
              <CardTitle className="text-sm font-medium">
                {t("dashboard.roll.level")}
              </CardTitle>
              {roloAcabando ? (
                <Badge variant="outline" className={statusBadgeClasses.warning}>
                  <AlertTriangle className="h-3 w-3 mr-1" />
                  {t("dashboard.roll.ending")}
                </Badge>
              ) : (
                <Badge variant="outline" className={statusBadgeClasses.success}>
                  {t("common.ok")}
                </Badge>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="h-2.5 w-full rounded-full bg-muted overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${
                  percentualRolo >= 90
                    ? "bg-red-500"
                    : percentualRolo >= 70
                      ? "bg-amber-500"
                      : "bg-emerald-500"
                }`}
                style={{ width: `${percentualRolo}%` }}
              />
            </div>
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>
                <strong className="text-foreground">
                  {etiquetasImpressas.toLocaleString(localeData)}
                </strong>{" "}
                {t("dashboard.roll.usedOf")}{" "}
                <strong className="text-foreground">
                  {tamanhoRolo.toLocaleString(localeData)}
                </strong>{" "}
                {t("dashboard.roll.labelsUsed")}
              </span>
              <span>
                <strong className="text-foreground">
                  {restanteRolo.toLocaleString(localeData)}
                </strong>{" "}
                {t("dashboard.roll.remaining")} ({100 - percentualRolo}%)
              </span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Quick actions */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium">
            {t("dashboard.quickActions")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
            {quickActions.map((action) => {
              const Icon = action.icon;
              return (
                <Link
                  key={action.href}
                  to={action.href}
                  className="group relative flex items-start gap-3 rounded-md border bg-card p-3 hover:bg-accent hover:border-primary/30 transition-colors"
                >
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-muted">
                    <Icon className={`h-4 w-4 ${action.accent}`} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium leading-tight truncate">
                      {t(action.labelKey)}
                    </p>
                    <p className="text-[11px] text-muted-foreground truncate">
                      {t(action.descKey)}
                    </p>
                  </div>
                  <ChevronRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity self-center shrink-0" />
                </Link>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Duas colunas: Últimas impressões + Fila */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="flex flex-col">
          <CardHeader className="pb-3 flex flex-row items-center justify-between space-y-0">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Clock className="h-4 w-4 text-muted-foreground" />
              {t("dashboard.recentPrints")}
            </CardTitle>
            <Button asChild variant="ghost" size="sm" className="h-7 text-xs">
              <Link to="/logs-impressao">
                {t("common.viewAll")}
                <ArrowRight className="h-3 w-3 ml-1" />
              </Link>
            </Button>
          </CardHeader>
          <CardContent className="flex-1">
            {loadingStats ? (
              <div className="space-y-2">
                {Array.from({ length: 4 }).map((_, i) => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            ) : ultimasOrdens.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-40 text-muted-foreground">
                <Inbox className="h-8 w-8 mb-2 opacity-50" />
                <p className="text-sm">{t("dashboard.noRecentPrints")}</p>
              </div>
            ) : (
              <ScrollArea className="h-[260px] pr-2">
                <ul className="space-y-2">
                  {ultimasOrdens.map((ordem, i) => {
                    const variant = getStatusVariant(ordem.estado_da_ordem);
                    return (
                      <li
                        key={`${ordem.nome_da_ordem}-${i}`}
                        className="flex items-center justify-between gap-2 rounded-md border p-2.5 hover:bg-muted/40 transition-colors"
                      >
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium truncate">
                            {ordem.nome_da_ordem}
                          </p>
                          <p className="text-[11px] text-muted-foreground truncate">
                            {ordem.etiquetas_total?.length ?? 0}{" "}
                            {t("dashboard.labels")} ·{" "}
                            {ordem.data_ultima_atualizacao
                              ? formatarData(ordem.data_ultima_atualizacao)
                              : "—"}
                          </p>
                        </div>
                        <Badge
                          variant="outline"
                          className={statusBadgeClasses[variant]}
                        >
                          {ordem.estado_da_ordem || "—"}
                        </Badge>
                      </li>
                    );
                  })}
                </ul>
              </ScrollArea>
            )}
          </CardContent>
        </Card>

        <Card className="flex flex-col">
          <CardHeader className="pb-3 flex flex-row items-center justify-between space-y-0">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Inbox className="h-4 w-4 text-muted-foreground" />
              {t("dashboard.printQueue")}
              {itensFila.length > 0 && (
                <Badge variant="secondary" className="h-5 px-1.5 text-[10px]">
                  {itensFila.length}
                </Badge>
              )}
            </CardTitle>
            <Button asChild variant="ghost" size="sm" className="h-7 text-xs">
              <Link to="/fila-impressao-socket">
                {t("common.manage")}
                <ArrowRight className="h-3 w-3 ml-1" />
              </Link>
            </Button>
          </CardHeader>
          <CardContent className="flex-1">
            {itensFila.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-40 text-muted-foreground">
                <CheckCircle2 className="h-8 w-8 mb-2 opacity-50 text-emerald-500" />
                <p className="text-sm">{t("dashboard.queueEmpty")}</p>
                <p className="text-[11px]">{t("dashboard.queueEmptyDesc")}</p>
              </div>
            ) : (
              <ScrollArea className="h-[260px] pr-2">
                <ul className="space-y-2">
                  {itensFila.slice(0, 10).map((item, i) => (
                    <li
                      key={`${item.nome_da_ordem}-${i}`}
                      className="flex items-center justify-between gap-2 rounded-md border p-2.5 hover:bg-muted/40 transition-colors"
                    >
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        <div className="flex h-6 w-6 items-center justify-center rounded bg-blue-500/10 text-blue-600 dark:text-blue-400 text-[11px] font-medium shrink-0">
                          {i + 1}
                        </div>
                        <p className="text-sm font-medium truncate">
                          {item.nome_da_ordem}
                        </p>
                      </div>
                      <Badge
                        variant="outline"
                        className={statusBadgeClasses.info}
                      >
                        {t("dashboard.pending")}
                      </Badge>
                    </li>
                  ))}
                  {itensFila.length > 10 && (
                    <li className="text-[11px] text-center text-muted-foreground py-1">
                      {t("dashboard.andMore", { count: itensFila.length - 10 })}
                    </li>
                  )}
                </ul>
              </ScrollArea>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Footer info: configuração atual */}
      <Card className="bg-muted/30">
        <CardContent className="p-3 flex flex-wrap items-center justify-between gap-3 text-xs text-muted-foreground">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
            <span className="flex items-center gap-1.5">
              <Printer className="h-3.5 w-3.5" />
              {t("dashboard.hardware")}:{" "}
              <span className="text-foreground">
                {impressaoConfig?.app_id || "—"}
              </span>
              {leitorAtual?.nm_leitor && (
                <span className="text-muted-foreground">
                  · {leitorAtual.nm_leitor}
                </span>
              )}
            </span>
            <span className="flex items-center gap-1.5">
              <Database className="h-3.5 w-3.5" />
              {t("dashboard.defaultLabel")}:{" "}
              <span className="text-foreground">{etiquetaPadraoNome}</span>
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            {loadingStats && (
              <>
                <Loader2 className="h-3 w-3 animate-spin" />
                <span>{t("common.loading")}</span>
              </>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
